import React, { useMemo, useState } from 'react'
import { ethers } from 'ethers'
import { useTranslation } from 'react-i18next'

import { getWalletSnapshot } from '../../../../utils/walletProviders'
import { nftAbi } from '../../../../utils/config'
import { getLocalizedWalletError } from '../../../NFTConsole/errorMessages'
import ConsoleDialog from '../../../NFTConsole/ConsoleDialog'

export default function WhitelistModal(props) {
    const [currentProvider, setCurrentProvider] = useState(null)
    const [currentTab, setCurrentTab] = useState('add')
    const [addressInput, setAddressInput] = useState('')
    const [errorMsg, setErrorMsg] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const { t } = useTranslation()

    const addressCount = useMemo(() => new Set(
        addressInput
            .split(/\r?\n/)
            .map((address) => address.trim().toLowerCase())
            .filter(Boolean)
    ).size, [addressInput])

    const initProvider = () => {
        if (currentProvider) return currentProvider
        const injectedProvider = getWalletSnapshot().provider
        if (!injectedProvider) throw new Error(t('请先连接钱包再操作'))
        const provider = new ethers.providers.Web3Provider(injectedProvider)
        setCurrentProvider(provider)
        return provider
    }

    const selectMode = (mode) => {
        setCurrentTab(mode)
        setErrorMsg('')
    }

    const submit = async () => {
        setErrorMsg('')
        const addresses = []
        const seen = new Set()
        const lines = addressInput.split(/\r?\n/)

        for (let index = 0; index < lines.length; index += 1) {
            const address = lines[index].trim()
            if (!address) continue
            if (!ethers.utils.isAddress(address)) {
                setErrorMsg(t('第1行的地址格式不正确').replace('{line}', index + 1))
                return
            }
            const normalized = ethers.utils.getAddress(address)
            if (seen.has(normalized.toLowerCase())) continue
            seen.add(normalized.toLowerCase())
            addresses.push(normalized)
        }

        if (addresses.length === 0) {
            setErrorMsg(t('请至少输入一个钱包地址'))
            return
        }

        setSubmitLoading(true)
        try {
            const provider = initProvider()
            const contract = new ethers.Contract(props.poolAddress, nftAbi, provider.getSigner())
            const transaction = await contract.multiWLs(addresses, currentTab === 'add')
            await transaction.wait()
            setAddressInput('')
            props.ok?.()
        } catch (error) {
            setErrorMsg(getLocalizedWalletError(error, t))
        } finally {
            setSubmitLoading(false)
        }
    }

    return <ConsoleDialog
        show={props.show}
        onClose={() => !submitLoading && props.handleClose?.()}
        title={t('设置白名单')}
        description={t('批量添加或移除可直接参与推广的钱包')}
        icon="fa-list-alt"
        closeLabel={t('关闭')}
        className="whitelist-dialog"
        footer={<>
            <button type="button" className="console-button is-secondary" disabled={submitLoading} onClick={() => props.handleClose?.()}>{t('取消')}</button>
            <button type="button" className="console-button is-primary" disabled={submitLoading || addressCount === 0} onClick={submit}>
                {submitLoading && <i className="fa fa-spinner loading-css" />} {currentTab === 'add' ? t('添加白名单') : t('移除白名单')}
            </button>
        </>}
    >
        <div className="whitelist-intro">
            <span><i className="fa fa-bolt" /></span>
            <div>
                <strong>{t('白名单说明')}</strong>
                <p>{t('白名单地址可直接参与邀请推广，无需先铸造 NFT。普通地址需要先完成铸造后才能邀请。')}</p>
            </div>
        </div>

        <div className="whitelist-mode-switch" aria-label={t('当前操作')}>
            <button type="button" aria-pressed={currentTab === 'add'} className={currentTab === 'add' ? 'is-active' : ''} onClick={() => selectMode('add')}>
                <i className="fa fa-user-plus" /> {t('添加白名单')}
            </button>
            <button type="button" aria-pressed={currentTab === 'remove'} className={currentTab === 'remove' ? 'is-active is-danger' : ''} onClick={() => selectMode('remove')}>
                <i className="fa fa-user-minus" /> {t('移除白名单')}
            </button>
        </div>

        <label className="whitelist-editor">
            <span>
                <strong>{t('批量钱包地址')}</strong>
                <small>{addressCount} {t('个地址')}</small>
            </span>
            <textarea
                value={addressInput}
                onChange={(event) => { setAddressInput(event.target.value); setErrorMsg('') }}
                placeholder={t('输入要设置的钱包地址, 每行一个地址')}
                rows={7}
                spellCheck="false"
            />
            <small>{t('每行一个地址，系统会自动忽略空行和重复地址')}</small>
        </label>

        {errorMsg && <div className="console-inline-error" role="alert"><i className="fa fa-exclamation-circle" /> {errorMsg}</div>}
    </ConsoleDialog>
}
